import { animate } from "motion";
import { crossfadeMedia, pauseVideosIn } from "../lib/media-crossfade.js";
import { requestScrollLock, releaseScrollLock } from "../lib/scroll-lock.js";

const MD_BREAKPOINT = 768;
const TRANSITION_DURATION = 0.6;
const SPRING_EASING = {
  type: "spring",
  stiffness: 100,
  damping: 20,
  mass: 1
};

const TEXT_EASING = {
  type: "spring",
  stiffness: 250,
  damping: 20,
  mass: 1
};

const WHEEL_COOLDOWN = 600;
const WHEEL_THRESHOLD = 30;

class ProductShowcase extends HTMLElement {
  connectedCallback() {
    this.activeIndex = parseInt(this.dataset.activeIndex, 10) || 0;
    this.productCount = parseInt(this.dataset.productCount, 10) || 0;

    if (this.productCount === 0) return;

    this.activeImageIndices = new Array(this.productCount).fill(0);
    this.isTransitioning = false;
    this._transitionGen = 0;
    this.isLocked = false;
    this.lastWheelTime = 0;

    this.abortController = new AbortController();

    this.cacheElements();
    this.desktopQuery = window.matchMedia(`(min-width: ${MD_BREAKPOINT}px)`);

    this.bindWheel();
    this.bindNav();
    this.bindNavScroll();
    this.bindThumbnails();
    this.bindMobileGalleries();
    this.bindScrollToIndex();
    this.bindSizeSelector();
    this.bindDetailsModal();
    this.bindAddToCart();
    this.setupIntersectionObserver();
    this.initNavPosition();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.abortController?.abort();
  }

  cacheElements() {
    this.taglines = [...this.querySelectorAll("[data-tagline]")];
    this.subtaglines = [...this.querySelectorAll("[data-subtagline]")];
    this.purchaseTitles = [...this.querySelectorAll("[data-purchase-title]")];
    this.productImageContainers = [...this.querySelectorAll("[data-product-images]")];
    this.navItems = [...this.querySelectorAll("[data-nav-item]")];
    this.counterItems = [...this.querySelectorAll("[data-counter-item]")];
    this.thumbStrips = [...this.querySelectorAll("[data-thumb-strip]")];
    this.dotStrips = [...this.querySelectorAll("[data-dot-strip]")];

    this.purchaseEl = this.querySelector("[data-purchase]");
    this.purchaseSingleVariant = this.querySelector("[data-purchase-single-variant]");
    this.purchaseVariantSelector = this.querySelector("[data-purchase-variant-selector]");
    this.purchaseVariantId = this.querySelector("[data-variant-id]");
    this.purchaseSubmit = this.querySelector("[data-purchase-submit]");
    this.purchaseSubmitText = this.querySelector("[data-purchase-submit-text]");
    this.purchaseDetailsLink = this.querySelector("[data-purchase-details-link]");
    this.purchaseVariantSelectorText = this.querySelector("[data-purchase-variant-selector-text]");
    this.purchaseVariantSelectorChevron = this.querySelector("[data-purchase-variant-selector-chevron]");

    this.sizePopup = this.querySelector("[data-size-popup]");
    this.sizePopupGrid = this.querySelector("[data-size-popup-grid]");

    this.detailsDialog = this.querySelector("[data-details-dialog]");
    this.detailsClose = this.querySelector("[data-details-close]");
    this.detailsTitle = this.querySelector("[data-details-title]");
    this.detailsDescription = this.querySelector("[data-details-description]");

    const dataScript = this.querySelector("[data-purchase-data]");
    this.purchaseData = dataScript ? JSON.parse(dataScript.textContent) : [];
    this.selectedVariants = new Array(this.productCount).fill(null);
  }

  isDesktop() {
    return this.desktopQuery.matches;
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio === 1) {
            this.lock();
          } else {
            this.unlock();
          }
        }
      },
      { threshold: [0, 1] }
    );

    this.observer.observe(this);
  }

  lock() {
    if (this.isLocked) return;
    this.isLocked = true;
    requestScrollLock("product-showcase");
  }

  unlock() {
    if (!this.isLocked) return;
    this.isLocked = false;
    releaseScrollLock("product-showcase");
  }

  bindWheel() {
    this.addEventListener(
      "wheel",
      (e) => {
        if (!this.isLocked) return;
        if (this._navScrolling) return;

        const now = Date.now();
        if (now - this.lastWheelTime < WHEEL_COOLDOWN) return;

        const delta = e.deltaY;
        if (Math.abs(delta) < WHEEL_THRESHOLD) return;

        e.preventDefault();

        if (delta > 0) {
          if (this.activeIndex < this.productCount - 1) {
            this.lastWheelTime = now;
            this.switchProduct(this.activeIndex + 1);
          } else {
            this.unlock();
          }
        } else {
          if (this.activeIndex > 0) {
            this.lastWheelTime = now;
            this.switchProduct(this.activeIndex - 1);
          } else {
            this.unlock();
            window.scrollBy({ top: -100, behavior: "smooth" });
          }
        }
      },
      { passive: false }
    );

    let touchStartY = 0;
    let touchStartX = 0;
    let touchDirection = null;
    let touchInGallery = false;

    this.addEventListener(
      "touchstart",
      (e) => {
        touchStartY = e.touches[0].clientY;
        touchStartX = e.touches[0].clientX;
        touchDirection = null;
        touchInGallery = !!e.target.closest("[data-mobile-gallery]");
      },
      { passive: true }
    );

    this.addEventListener(
      "touchmove",
      (e) => {
        if (this.isDesktop() || touchInGallery) return;

        const deltaY = Math.abs(e.touches[0].clientY - touchStartY);
        const deltaX = Math.abs(e.touches[0].clientX - touchStartX);

        if (!touchDirection && (deltaX > 10 || deltaY > 10)) {
          touchDirection = deltaY > deltaX ? "vertical" : "horizontal";
        }

        if (touchDirection === "vertical") {
          e.preventDefault();
        }
      },
      { passive: false }
    );

    this.addEventListener(
      "touchend",
      (e) => {
        if (this.isDesktop()) return;

        const now = Date.now();
        if (now - this.lastWheelTime < WHEEL_COOLDOWN) return;

        const deltaY = touchStartY - e.changedTouches[0].clientY;
        const deltaX = touchStartX - e.changedTouches[0].clientX;
        if (Math.abs(deltaY) < 50 || Math.abs(deltaY) <= Math.abs(deltaX)) return;

        if (deltaY > 0 && this.activeIndex < this.productCount - 1) {
          this.lastWheelTime = now;
          this.switchProduct(this.activeIndex + 1);
        } else if (deltaY < 0 && this.activeIndex > 0) {
          this.lastWheelTime = now;
          this.switchProduct(this.activeIndex - 1);
        } else if (deltaY > 0 && this.activeIndex === this.productCount - 1) {
          this.unlock();
        } else if (deltaY < 0 && this.activeIndex === 0) {
          this.unlock();
          window.scrollBy({ top: -100, behavior: "smooth" });
        }
      },
      { passive: true }
    );
  }

  switchProduct(newIndex) {
    if (newIndex === this.activeIndex || newIndex < 0 || newIndex >= this.productCount || this.isTransitioning) return;

    this.isTransitioning = true;
    const transitionGen = ++this._transitionGen;
    const prevIndex = this.activeIndex;
    const direction = newIndex > prevIndex ? 1 : -1;

    const textTransitionPromises = [
      this.animateFadeTransition(this.taglines, prevIndex, newIndex),
      this.animateFadeTransition(this.subtaglines, prevIndex, newIndex),
      this.animateFadeTransition(this.purchaseTitles, prevIndex, newIndex),
      this.switchCounter(this.counterItems, prevIndex, newIndex, direction)
    ].filter(Boolean);

    this.crossfadeElement(this.productImageContainers, prevIndex, newIndex, direction);
    this.updatePurchase(newIndex);
    this.switchThumbnails(prevIndex, newIndex);
    this.switchDots(prevIndex, newIndex);
    this.updateNav(newIndex);

    this.activeIndex = newIndex;
    this.dataset.activeIndex = String(newIndex);

    this.resetMobileGallery(newIndex);
    this.updateUrl(newIndex);

    Promise.all(textTransitionPromises).finally(() => {
      if (transitionGen !== this._transitionGen) return;

      this.commitFadeStackState(this.taglines, newIndex);
      this.commitFadeStackState(this.subtaglines, newIndex);
      this.commitFadeStackState(this.purchaseTitles, newIndex);
      this.commitCounterStackState(this.counterItems, newIndex);

      this.isTransitioning = false;
      if (this.activeIndex === this.productCount - 1) {
        this.unlock();
      }
    });
  }

  updateUrl(index) {
    const container = this.productImageContainers[index];
    if (!container) return;

    const url = container.dataset.productUrl;
    const title = container.dataset.productTitle;
    if (url) {
      history.replaceState({}, title || "", url);
      if (title) document.title = title;
    }
  }

  commitFadeStackState(elements, activeIndex) {
    elements.forEach((el, i) => {
      el.getAnimations().forEach((a) => a.cancel());
      if (i === activeIndex) {
        el.style.position = "relative";
        el.style.inset = "";
        el.style.opacity = "1";
        el.removeAttribute("aria-hidden");
      } else {
        el.style.position = "absolute";
        el.style.inset = "0";
        el.style.opacity = "0";
        el.setAttribute("aria-hidden", "true");
      }
    });
  }

  // Fade transition for taglines and subtaglines
  animateFadeTransition(elements, prevIndex, newIndex) {
    const outgoing = elements[prevIndex];
    const incoming = elements[newIndex];
    if (!outgoing || !incoming) return Promise.resolve();

    elements.forEach((el, i) => {
      if (i !== prevIndex && i !== newIndex) {
        el.getAnimations().forEach((a) => a.cancel());
        el.style.opacity = "0";
        el.style.position = "absolute";
        el.style.inset = "0";
        el.setAttribute("aria-hidden", "true");
      }
    });

    outgoing.getAnimations().forEach((a) => a.cancel());
    outgoing.style.position = "absolute";
    outgoing.style.inset = "0";
    outgoing.setAttribute("aria-hidden", "true");
    const outAnim = animate(outgoing, { opacity: 0 }, { duration: 0.1, ...TEXT_EASING });

    incoming.getAnimations().forEach((a) => a.cancel());
    incoming.style.position = "relative";
    incoming.style.inset = "";
    incoming.style.opacity = "0";
    incoming.removeAttribute("aria-hidden");
    const inAnim = animate(incoming, { opacity: 1 }, { duration: 0.1, ...TEXT_EASING });

    return Promise.all([outAnim.finished, inAnim.finished]);
  }

  // Manages visibility of stacked counter number elements — keeps the active one
  // in normal flow (position: relative) and all others out of flow (position: absolute)
  // so only the current product's number is visible without affecting layout.
  commitCounterStackState(elements, activeIndex) {
    elements.forEach((el, i) => {
      el.getAnimations().forEach((a) => a.cancel());
      if (i === activeIndex) {
        el.style.position = "relative";
        el.style.inset = "";
        el.style.opacity = "1";
        el.style.transform = "translateY(0%)";
        el.removeAttribute("aria-hidden");
      } else {
        el.style.position = "absolute";
        el.style.inset = "0";
        el.style.opacity = "0";
        el.style.transform = "translateY(100%)";
        el.setAttribute("aria-hidden", "true");
      }
    });
  }

  switchCounter(elements, prevIndex, newIndex, direction) {
    const outgoing = elements[prevIndex];
    const incoming = elements[newIndex];
    if (!outgoing || !incoming) return Promise.resolve();

    elements.forEach((el, i) => {
      if (i !== prevIndex && i !== newIndex) {
        el.getAnimations().forEach((a) => a.cancel());
        el.style.opacity = "0";
        el.style.transform = "translateY(100%)";
        el.style.position = "absolute";
        el.style.inset = "0";
        el.setAttribute("aria-hidden", "true");
      }
    });

    outgoing.getAnimations().forEach((a) => a.cancel());
    outgoing.style.position = "absolute";
    outgoing.style.inset = "0";
    outgoing.setAttribute("aria-hidden", "true");
    const outAnim = animate(
      outgoing,
      { opacity: 0, transform: `translateY(${-direction * 100}%)` },
      { duration: TRANSITION_DURATION, ...SPRING_EASING }
    );

    incoming.getAnimations().forEach((a) => a.cancel());
    incoming.style.position = "relative";
    incoming.style.inset = "";
    incoming.style.transform = `translateY(${direction * 100}%)`;
    incoming.style.opacity = "0";
    incoming.removeAttribute("aria-hidden");
    const inAnim = animate(
      incoming,
      { opacity: 1, transform: "translateY(0%)" },
      { duration: TRANSITION_DURATION, ...SPRING_EASING }
    );

    return Promise.all([outAnim.finished, inAnim.finished]);
  }

  crossfadeElement(containers, prevIndex, newIndex, direction) {
    const gen = this._transitionGen;
    crossfadeMedia(containers, prevIndex, newIndex, direction, () => {
      if (gen !== this._transitionGen) return;
      this.playActiveVideo(newIndex);
    });
  }

  updatePurchase(newIndex) {
    const data = this.purchaseData[newIndex];
    if (!data || !this.purchaseEl) return;

    this.closeSizePopup();
    this.closeDetailsModal();

    const multipleVariants = data.multipleVariants > 1;
    this.purchaseSingleVariant.classList.toggle("hidden", multipleVariants);
    this.purchaseVariantSelector.classList.toggle("hidden", !multipleVariants);

    if (data.comingSoon) {
      this.purchaseSubmit.disabled = true;
      this.purchaseSubmitText.textContent = "Coming soon";
    } else {
      const selected = this.selectedVariants[newIndex];
      if (selected) {
        this.purchaseVariantId.value = selected.id;
        this.purchaseVariantSelectorText.textContent = selected.option1;
        this.purchaseSubmit.disabled = !selected.available;
        this.purchaseSubmitText.textContent = selected.available ? "Add to cart" : "Sold out";
      } else {
        this.purchaseVariantId.value = data.variantId;
        this.purchaseVariantSelectorText.textContent = "Size";
        this.purchaseSubmit.disabled = multipleVariants || !data.available;
        this.purchaseSubmitText.textContent = !data.available
          ? "Sold out"
          : multipleVariants
            ? "Select size"
            : "Add to cart";
      }
    }

    this.purchaseDetailsLink.href = data.url;
    this.purchaseDetailsLink.classList.toggle("hidden", !data.description);
  }

  switchThumbnails(prevIndex, newIndex) {
    const outgoing = this.thumbStrips[prevIndex];
    const incoming = this.thumbStrips[newIndex];

    if (outgoing) {
      outgoing.getAnimations().forEach((a) => a.cancel());
      animate(outgoing, { opacity: 0 }, { duration: TRANSITION_DURATION, ...SPRING_EASING }).finished.then(() => {
        outgoing.style.display = "none";
      });
    }

    if (incoming) {
      incoming.style.display = "";
      incoming.style.opacity = "0";
      incoming.getAnimations().forEach((a) => a.cancel());
      animate(incoming, { opacity: 1 }, { duration: TRANSITION_DURATION, ...SPRING_EASING });
      this.activeImageIndices[newIndex] = 0;
      this.updateThumbActive(incoming, 0);
      this.showDesktopImage(newIndex, 0);
    }
  }

  switchDots(prevIndex, newIndex) {
    const outgoing = this.dotStrips.find((s) => parseInt(s.dataset.dotStrip, 10) === prevIndex);
    const incoming = this.dotStrips.find((s) => parseInt(s.dataset.dotStrip, 10) === newIndex);

    if (outgoing) outgoing.style.display = "none";
    if (incoming) incoming.style.display = "";
  }

  updateNav(newIndex) {
    this.navItems.forEach((item, i) => {
      if (i === newIndex) {
        item.classList.remove("text-ui-content-secondary");
        item.classList.add("text-ui-content-primary", "font-bold");
      } else {
        item.classList.remove("text-ui-content-primary", "font-bold");
        item.classList.add("text-ui-content-secondary");
      }
    });

    this.scrollNavTo(newIndex);
  }

  initNavPosition() {
    const nav = this.querySelector("[data-product-nav]");
    if (!nav || this.navItems.length < 2) return;

    const visibleCount = 9;
    const centerSlot = Math.floor(visibleCount / 2);
    const itemHeight = this.navItems[1].offsetTop - this.navItems[0].offsetTop;
    const maxOffset = Math.max(0, (this.productCount - visibleCount) * itemHeight);
    const idealOffset = (this.activeIndex - centerSlot) * itemHeight;
    const offset = Math.max(0, Math.min(idealOffset, maxOffset));

    this._navOffset = offset;
    nav.style.transform = `translateY(${-offset}px)`;
  }

  scrollNavTo(index) {
    const nav = this.querySelector("[data-product-nav]");
    if (!nav || this.navItems.length < 2) return;

    const visibleCount = 9;
    const centerSlot = Math.floor(visibleCount / 2);
    const itemHeight = this.navItems[1].offsetTop - this.navItems[0].offsetTop;
    const maxOffset = Math.max(0, (this.productCount - visibleCount) * itemHeight);
    const idealOffset = (index - centerSlot) * itemHeight;
    const offset = Math.max(0, Math.min(idealOffset, maxOffset));

    this._navOffset = offset;
    animate(nav, { transform: `translateY(${-offset}px)` }, { duration: TRANSITION_DURATION, ...SPRING_EASING });
  }

  bindNav() {
    this.navItems.forEach((item) => {
      const index = parseInt(item.dataset.navItem, 10);
      item.addEventListener("click", () => {
        this.switchProduct(index);
      });
    });
  }

  bindNavScroll() {
    const wrapper = this.querySelector("[data-product-nav-wrapper]");
    const nav = this.querySelector("[data-product-nav]");
    if (!wrapper || !nav || this.navItems.length < 2) return;

    this._navScrolling = false;
    let scrollTimeout = null;

    wrapper.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();
        e.stopPropagation();

        this._navScrolling = true;
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          this._navScrolling = false;
        }, WHEEL_COOLDOWN);

        const itemHeight = this.navItems[1].offsetTop - this.navItems[0].offsetTop;
        const maxOffset = Math.max(0, nav.scrollHeight - wrapper.offsetHeight);
        const step = itemHeight;

        if (typeof this._navOffset === "undefined") this._navOffset = 0;

        if (e.deltaY > 0) {
          this._navOffset = Math.min(this._navOffset + step, maxOffset);
        } else {
          this._navOffset = Math.max(this._navOffset - step, 0);
        }

        animate(nav, { transform: `translateY(${-this._navOffset}px)` }, { duration: 0.3, easing: "ease-out" });
      },
      { passive: false }
    );
  }

  bindThumbnails() {
    this.thumbStrips.forEach((strip, productIndex) => {
      const thumbs = [...strip.querySelectorAll("[data-thumb]")];
      thumbs.forEach((thumb) => {
        const imageIndex = parseInt(thumb.dataset.thumb, 10);
        thumb.addEventListener("click", () => {
          this.activeImageIndices[productIndex] = imageIndex;
          this.showDesktopImage(productIndex, imageIndex);
          this.updateThumbActive(strip, imageIndex);
        });
      });
    });
  }

  showDesktopImage(productIndex, imageIndex) {
    const container = this.productImageContainers[productIndex];
    if (!container) return;

    const gallery = container.querySelector("[data-desktop-gallery]");
    if (!gallery) return;

    const slides = [...gallery.querySelectorAll("[data-image]")];
    slides.forEach((slide, i) => {
      slide.style.opacity = i === imageIndex ? "1" : "0";
      const video = slide.querySelector("video");
      if (!video) return;
      if (i === imageIndex) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  updateThumbActive(strip, activeIndex) {
    const thumbs = [...strip.querySelectorAll("[data-thumb]")];
    thumbs.forEach((thumb, i) => {
      if (i === activeIndex) {
        thumb.classList.add("border", "border-ui-content-primary/25");
        thumb.classList.remove("opacity-60");
      } else {
        thumb.classList.remove("border", "border-ui-content-primary/25");
        thumb.classList.add("opacity-60");
      }
    });
  }

  bindMobileGalleries() {
    this.productImageContainers.forEach((container, productIndex) => {
      const gallery = container.querySelector("[data-mobile-gallery]");
      if (!gallery) return;

      let scrollTimer = null;
      gallery.addEventListener(
        "scroll",
        () => {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(() => {
            this.onMobileGalleryScroll(gallery, productIndex);
          }, 100);
        },
        { passive: true }
      );
    });
  }

  onMobileGalleryScroll(gallery, productIndex) {
    const slides = [...gallery.children];
    if (slides.length === 0) return;

    const containerLeft = gallery.scrollLeft;
    const containerWidth = gallery.offsetWidth;
    const center = containerLeft + containerWidth / 2;

    let closestIndex = 0;
    let closestDistance = Infinity;

    for (let i = 0; i < slides.length; i++) {
      const slideCenter = slides[i].offsetLeft + slides[i].offsetWidth / 2;
      const distance = Math.abs(center - slideCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    }

    this.updateDots(productIndex, closestIndex);
  }

  updateDots(productIndex, activeImageIndex) {
    const dotStrip = this.dotStrips.find((strip) => parseInt(strip.dataset.dotStrip, 10) === productIndex);
    if (!dotStrip) return;

    const dots = [...dotStrip.querySelectorAll("[data-dot]")];
    dots.forEach((dot, i) => {
      if (i === activeImageIndex) {
        dot.classList.remove("border", "border-ui-content-primary/25");
        dot.classList.add("bg-ui-content-primary");
      } else {
        dot.classList.remove("bg-ui-content-primary");
        dot.classList.add("border", "border-ui-content-primary/25");
      }
    });
  }

  resetMobileGallery(productIndex) {
    const container = this.productImageContainers[productIndex];
    if (!container) return;

    const gallery = container.querySelector("[data-mobile-gallery]");
    if (!gallery) return;

    gallery.scrollTo({ left: 0, behavior: "auto" });
    this.updateDots(productIndex, 0);
  }

  pauseVideos(container) {
    pauseVideosIn(container);
  }

  playActiveVideo(productIndex) {
    const container = this.productImageContainers[productIndex];
    if (!container) return;

    const gallery = container.querySelector("[data-desktop-gallery]");
    if (!gallery) return;

    const activeSlide = gallery.querySelector(`[data-image="${this.activeImageIndices[productIndex] || 0}"]`);
    const video = activeSlide?.querySelector("video");
    if (video) video.play().catch(() => {});
  }

  bindSizeSelector() {
    if (!this.purchaseVariantSelector || !this.sizePopup) return;

    this.purchaseVariantSelector.addEventListener("click", (e) => {
      e.stopPropagation();
      if (this.sizePopup.hidden) {
        this.openSizePopup();
      } else {
        this.closeSizePopup();
      }
    });

    document.addEventListener(
      "click",
      (e) => {
        if (
          !this.sizePopup.hidden &&
          !this.sizePopup.contains(e.target) &&
          !this.purchaseVariantSelector.contains(e.target)
        ) {
          this.closeSizePopup();
        }
      },
      { signal: this.abortController.signal }
    );

    document.addEventListener(
      "keydown",
      (e) => {
        if (e.key === "Escape" && !this.sizePopup.hidden) {
          this.closeSizePopup();
        }
      },
      { signal: this.abortController.signal }
    );

    // Size guide link inside the popup is handled by bindDetailsModal()
  }

  openSizePopup() {
    this.populateSizeGrid(this.activeIndex);
    this.sizePopup.hidden = false;
    this.purchaseVariantSelectorChevron?.classList.add("rotate-180");
  }

  closeSizePopup() {
    if (!this.sizePopup) return;
    this.sizePopup.hidden = true;
    this.purchaseVariantSelectorChevron?.classList.remove("rotate-180");
  }

  populateSizeGrid(productIndex) {
    if (!this.sizePopupGrid) return;

    const data = this.purchaseData[productIndex];
    if (!data?.variants) return;

    this.sizePopupGrid.innerHTML = "";
    const selected = this.selectedVariants[productIndex];

    const count = data.variants.length;
    const cols = count <= 4 ? count : Math.ceil(count / 2);
    const basis = `calc(${100 / cols}% - ${((cols - 1) * 4) / cols}px)`;

    for (const variant of data.variants) {
      const btn = document.createElement("button");
      btn.type = "button";
      const isSelected = selected && selected.id === variant.id;

      let classes =
        "flex h-[40px] cursor-pointer items-center justify-center rounded-full border px-3 py-2 text-[14px] transition-colors duration-150";

      if (isSelected) {
        classes += " border-ui-content-primary text-ui-content-primary";
      } else if (variant.available) {
        classes += " border-ui-content-primary/25 text-ui-content-primary hover:bg-gray-50";
      } else {
        classes += " border-ui-content-primary/25 text-ui-content-primary/40 cursor-not-allowed line-through";
      }

      btn.className = classes;
      btn.style.flex = `1 0 ${basis}`;
      btn.textContent = variant.option1;
      btn.addEventListener("click", () => this.selectVariant(productIndex, variant));
      this.sizePopupGrid.appendChild(btn);
    }
  }

  selectVariant(productIndex, variant) {
    const data = this.purchaseData[productIndex];
    if (data?.comingSoon) {
      this.purchaseSubmit.disabled = true;
      this.purchaseSubmitText.textContent = "Coming soon";
      this.closeSizePopup();
      return;
    }
    if (!variant.available) {
      this.purchaseSubmit.disabled = true;
      return;
    }
    this.selectedVariants[productIndex] = variant;
    this.purchaseVariantId.value = variant.id;
    this.purchaseVariantSelectorText.textContent = variant.option1;
    this.purchaseSubmit.disabled = !variant.available;
    this.purchaseSubmitText.textContent = variant.available ? "Add to cart" : "Sold out";
    this.closeSizePopup();
  }

  bindDetailsModal() {
    if (!this.detailsDialog) return;

    this.purchaseDetailsLink?.addEventListener("click", () => {
      this.openDetailsModal();
    });

    this.detailsClose?.addEventListener("click", () => this.closeDetailsModal());

    this.detailsDialog.addEventListener("click", (e) => {
      if (e.target === this.detailsDialog) this.closeDetailsModal();
    });

    // Route native Escape key through closeDetailsModal so the exit animation plays
    this.detailsDialog.addEventListener("cancel", (e) => {
      e.preventDefault();
      this.closeDetailsModal();
    });
  }

  openDetailsModal() {
    const data = this.purchaseData[this.activeIndex];
    if (!data || !this.detailsDialog) return;

    this.detailsTitle.textContent = data.title;
    this.detailsDescription.innerHTML = data.description || "";

    this.detailsDialog.classList.remove("is-closing");
    this.detailsDialog.showModal();
    this.detailsDialog.classList.add("flex");
  }

  closeDetailsModal() {
    if (!this.detailsDialog?.open || this.detailsDialog.classList.contains("is-closing")) return;
    this.detailsDialog.classList.add("is-closing");
    const onEnd = (e) => {
      if (e.animationName !== "details-dialog-exit") return;
      this.detailsDialog.removeEventListener("animationend", onEnd);
      this.detailsDialog.classList.remove("is-closing", "flex");
      this.detailsDialog.close();
    };
    this.detailsDialog.addEventListener("animationend", onEnd);
  }

  bindAddToCart() {
    const form = this.querySelector("[data-purchase-form]");
    if (!form) return;

    form.addEventListener("submit", async (e) => {
      e.preventDefault();

      if (this.purchaseSubmit?.disabled) return;
      this.purchaseSubmit.disabled = true;

      try {
        const res = await fetch(form.action, {
          method: "POST",
          body: new FormData(form)
        });

        if (!res.ok) throw new Error(res.statusText);

        await this.refreshHeaderSection();

        const overlay = document.querySelector("cart-overlay");
        if (overlay?.open) overlay.open();
      } catch (err) {
        console.error("Add to cart failed:", err);
      } finally {
        if (this.purchaseSubmit) this.purchaseSubmit.disabled = false;
      }
    });
  }

  async refreshHeaderSection() {
    try {
      const headerSection = document.querySelector(".header-section");
      if (!headerSection) return;

      const sectionId = headerSection.dataset.shopifySectionId || headerSection.id?.replace("shopify-section-", "");
      if (!sectionId) return;

      const res = await fetch(`${window.location.pathname}?sections=${sectionId}`);
      if (!res.ok) return;

      const json = await res.json();
      const html = json[sectionId];
      if (!html) return;

      headerSection.innerHTML = new DOMParser().parseFromString(html, "text/html").body.firstElementChild.innerHTML;
    } catch {
      // Cart was added but header refresh failed — overlay will show stale content
    }
  }

  bindScrollToIndex() {
    const links = this.querySelectorAll("[data-scroll-to-index], [data-scroll-to-index-mobile]");
    links.forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        this.unlock();
        const target = document.querySelector(link.getAttribute("href"));
        if (target) {
          target.scrollIntoView({ behavior: "smooth" });
        }
      });
    });
  }
}

if (!customElements.get("product-showcase")) {
  customElements.define("product-showcase", ProductShowcase);
}
